import React, { useCallback, useEffect, useState } from "react";
import { usePaymentDropdowns } from "../../hooks/usePaymentDropdowns";
import { Link, useHistory, useLocation } from "react-router-dom";
import { Row, Col, Card, Table, Button, Input, notification, AutoComplete, Modal, Select, Form, Typography, Checkbox, DatePicker, Spin, Tag, Space } from "antd";
import axios from "axios";
import { baseUrl } from "../../config";
import {
  DeleteOutlined, FilterOutlined, FileExcelOutlined,
  ExclamationCircleOutlined, SearchOutlined, ReloadOutlined,
  ArrowLeftOutlined, UndoOutlined
} from "@ant-design/icons";
import { getPermissions, hasPermission } from '../../components/permissions';
import '../../assets/styles/custom_style.css';
import moment from 'moment';
import * as XLSX from 'xlsx';
import Invoice from "../../components/Invoice";

const { confirm } = Modal;
const { Option } = Select;
const { Title, Text } = Typography;
const { Item } = Form;

function TrashPaymentList() {
  const { bankAccounts } = usePaymentDropdowns();
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const currentPageQuery = parseInt(queryParams.get('page')) || 1;
  const limitQuery = parseInt(queryParams.get('limit')) || 20;

  const [paymentList, setPaymentList] = useState([]);
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(currentPageQuery);
  const [limit, setLimit] = useState(limitQuery);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [optionSuggestion, setOptionSuggestion] = useState([]);
  const [showPaymentFilter, setShowPaymentFilter] = useState(true);
  const [sendPaynmentNotice, setSendPaynmentNotice] = useState(null);

  const txnDateRange = queryParams.get('txn_date_range') ? queryParams.get('txn_date_range').split(',') : [];
  const entryDateRange = queryParams.get('entry_date_range') ? queryParams.get('entry_date_range').split(',') : [];
  const monthRange = queryParams.get('month_range') ? queryParams.get('month_range').split(',') : [];

  const [filters, setFilters] = useState({
    name: queryParams.get('name') || '',
    phone: queryParams.get('phone') || '',
    amount: queryParams.get('amount') || '',
    receipt_no: queryParams.get('receipt_no') || '',
    e_reciept_no: queryParams.get('e_reciept_no') || '',
    e_reciept_date: queryParams.get('e_reciept_date') || '',
    utr_number: queryParams.get('utr_number') || '',
    bank_account: queryParams.get('bank_account') || '',
    special_occassion_category: queryParams.get('special_occassion_category') || '',
    transaction_date: queryParams.get('transaction_date') || '',
    pyament_mode_type: queryParams.get('pyament_mode_type') || '',
    payment_entry_date: queryParams.get('payment_entry_date') || '',
    payment_virify: queryParams.get('payment_virify') || '',
    month_range: monthRange,
    amountMin: queryParams.get('amountMin') || '',
    amountMax: queryParams.get('amountMax') || '',
    txn_date_range: txnDateRange,
    entry_date_range: entryDateRange,
  });

  // ─── Columns ───────────────────────────────────────────────────────────────
  const columns = [
    { title: "#", dataIndex: "key", key: "key", fixed: 'left', width: 50 },
    { title: "Name / Phone", dataIndex: "name", key: "name", fixed: 'left', width: 170 },
    { title: "Receipt No.", dataIndex: "receipt_no", key: "receipt_no", width: 110 },
    { title: "Amount", dataIndex: "amount", key: "amount", width: 110 },
    { title: "Txn (UTR/ID)", dataIndex: "utr_number", key: "utr_number", width: 140 },
    { title: "Txn Date", dataIndex: "transaction_date", key: "transaction_date", width: 130 },
    { title: "Duration", dataIndex: "duration_type", key: "duration_type", width: 90 },
    { title: "Monthly Share", dataIndex: "announced_amount", key: "announced_amount", width: 120 },
    { title: "Account", dataIndex: "bank_account", key: "bank_account", width: 200 },
    { title: "Pay Entry Date", dataIndex: "payment_entry_date", key: "payment_entry_date", width: 130 },
    { title: "Staff", dataIndex: "firstName", key: "firstName", width: 120 },
    { title: "Status", dataIndex: "payment_virify", key: "payment_virify", fixed: 'right', width: 100 },
    { title: "Action", dataIndex: "action", key: "action", fixed: 'right', width: 100 },
  ];

  // ─── Lifecycle ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const { state } = history.location;
    const restoredPage = state?.page || currentPage;
    const restoredLimit = state?.limit || limit;
    getPaymentList(restoredPage, restoredLimit, sortField, sortOrder, search, filters);
  }, [history.location]);

  // ─── API calls ─────────────────────────────────────────────────────────────
  async function getPaymentList(page = 1, lim = 20, sf = null, so = null, srch = '', flt = {}) {
    try {
      setLoading(true);
      setCurrentPage(page);
      setLimit(lim);
      const sortParam = sf && so ? `&sortField=${sf}&sortOrder=${so}` : '';
      const searchParam = srch ? `&search=${srch}` : '';
      const filterParams = Object.keys(flt).map(k => `${k}=${flt[k]}`).join('&');
      const response = await axios.get(
        `${baseUrl}/payments/trashlist?page=${page}&limit=${lim}${sortParam}${searchParam}&${filterParams}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } }
      );
      if (response.data.status) {
        setPaymentList(response.data.result);
        setPagination({ current: page, pageSize: lim, total: response.data.totalCount });
      } else {
        notification.info({ message: 'Info', description: response.data.message, placement: 'topRight' });
      }
    } catch (error) {
      notification.error({ message: 'Error', description: error.response?.data?.message || 'Failed to load data', placement: 'topRight' });
    } finally {
      setLoading(false);
    }
  }

  async function handleRestore(id) {
    try {
      const response = await axios.delete(`${baseUrl}/payments/restore/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
      });
      if (response.status === 200) {
        getPaymentList(pagination.current, pagination.pageSize, sortField, sortOrder, search, filters);
        notification.success({ message: 'Success', description: 'Payment restored successfully!', placement: 'topRight' });
      }
    } catch (error) {
      notification.error({ message: 'Error', description: error.response?.data?.message || 'Restore failed', placement: 'topRight' });
    }
  }

  const showRestoreConfirm = (id, name) => {
    confirm({
      title: 'Restore Payment?',
      icon: <ExclamationCircleOutlined style={{ color: '#52c41a' }} />,
      content: (
        <div>
          <p>Are you sure you want to restore this payment?</p>
          {name && <p><strong>{name}</strong></p>}
          <p style={{ color: '#52c41a' }}>The payment will move back to the active payment list.</p>
        </div>
      ),
      okText: 'Yes, Restore',
      okButtonProps: { style: { backgroundColor: '#52c41a', borderColor: '#52c41a' } },
      cancelText: 'Cancel',
      onOk() { handleRestore(id); },
    });
  };

  // ─── Table change ───────────────────────────────────────────────────────────
  const handleTableChange = (pag, _flt, sorter) => {
    const { current, pageSize } = pag;
    const { field, order } = sorter;
    const formattedOrder = order === 'ascend' ? 'asc' : 'desc';
    setSortField(field);
    setSortOrder(formattedOrder);
    history.push(`?page=${current}&limit=${pageSize}`);
    getPaymentList(current, pageSize, field, formattedOrder, search, filters);
  };

  const handleLimitChange = value => {
    setLimit(value);
    const updatedQueryParams = new URLSearchParams(location.search);
    updatedQueryParams.set('limit', value);
    updatedQueryParams.set('page', 1);
    history.push(`?${updatedQueryParams.toString()}`);
    getPaymentList(1, value, sortField, sortOrder, search, filters);
  };

  // ─── Filters ────────────────────────────────────────────────────────────────
  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
    const updatedQueryParams = new URLSearchParams(location.search);
    Object.keys(newFilters).forEach(key => {
      newFilters[key] ? updatedQueryParams.set(key, newFilters[key]) : updatedQueryParams.delete(key);
    });
    history.push(`?${updatedQueryParams.toString()}`);
  };

  const handleSearchReport = () => {
    getPaymentList(1, limit, sortField, sortOrder, search, filters);
  };

  const handleResetFilters = () => {
    const resetFilters = {
      name: '', phone: '', amount: '', receipt_no: '', e_reciept_no: '',
      e_reciept_date: '', utr_number: '', bank_account: '', special_occassion_category: '',
      transaction_date: '', pyament_mode_type: '', payment_entry_date: '', payment_virify: '',
      month_range: [], amountMin: '', amountMax: '', txn_date_range: [], entry_date_range: [],
    };
    setFilters(resetFilters);
    const updatedQueryParams = new URLSearchParams(location.search);
    Object.keys(resetFilters).forEach(key => updatedQueryParams.delete(key));
    history.push(`?${updatedQueryParams.toString()}`);
    getPaymentList(1, limit, sortField, sortOrder, search, resetFilters);
  };

  // ─── AutoComplete ───────────────────────────────────────────────────────────
  const getPanelValue = async (searchText, columnName, tableName) => {
    if (searchText.length < 3) { setOptionSuggestion([]); return; }
    try {
      const response = await axios.get(`${baseUrl}/payments/getSuggestionValue/`, {
        params: { q: searchText, column: columnName, table: tableName },
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
      });
      if (response.status === 200) setOptionSuggestion(response.data.result);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    }
  };

  // ─── Excel export ───────────────────────────────────────────────────────────
  const exportToExcel = async () => {
    setLoading(true);
    try {
      const sortParam = sortField && sortOrder ? `&sortField=${sortField}&sortOrder=${sortOrder}` : '';
      const searchParam = search ? `&search=${search}` : '';
      const filterParams = Object.keys(filters).map(k => `${k}=${filters[k]}`).join('&');
      const response = await axios.get(
        `${baseUrl}/payments/trashlist?page=1&limit=all${sortParam}${searchParam}&${filterParams}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } }
      );
      if (response.data.status) {
        const formattedData = response.data.result.map((p, i) => ({
          "Sr.No": i + 1,
          "Phone No": p.phone_no,
          "Name": p.name,
          "Relation": p.relation,
          "Father Name": p.father_name,
          "Village": p.per_village,
          "District": p.per_district,
          "Receipt No": p.receipt_no,
          "Amount": p.amount,
          "Txn (UTR/ID)": p.utr_number,
          "Txn Date": p.transaction_date ? moment(p.transaction_date).format('DD-MM-YYYY') : '-',
          "Start Month": p.start_month ? moment(p.start_month).format('MM-YYYY') : '-',
          "End Month": p.end_month ? moment(p.end_month).format('MM-YYYY') : '-',
          "Duration": p.duration_type,
          "Monthly Share": p.announced_amount,
          "Account": p.bank_account,
          "Pay Entry Date": p.payment_entry_date ? moment(p.payment_entry_date).format('DD-MM-YYYY') : '-',
          "C/R-Receipt": p.e_reciept_no,
          "C/R-Date": p.e_reciept_date ? moment(p.e_reciept_date).format('DD-MM-YYYY') : '-',
          "Payment Status": p.payment_virify,
        }));
        const worksheet = XLSX.utils.json_to_sheet(formattedData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Trash Payments");
        XLSX.writeFile(workbook, "Trash-Payments.xlsx");
      }
    } catch (error) {
      notification.error({ message: 'Error', description: 'Export failed', placement: 'topRight' });
    } finally {
      setLoading(false);
    }
  };

  const rowClassName = (record) =>
    record.is_payment_check === 'checked' ? 'checked-payment-row' : 'checked-row';

  const handleResendBtn = () => {
    setSendPaynmentNotice('');
    getPaymentList(pagination.current, pagination.pageSize, sortField, sortOrder, search, filters);
  };

  // ─── Status tag helper ──────────────────────────────────────────────────────
  const statusTag = (status) => {
    const map = {
      verified: { color: 'success', text: 'Verified' },
      pending:  { color: 'warning', text: 'Pending' },
      failed:   { color: 'error',   text: 'Failed' },
    };
    const cfg = map[status] || { color: 'default', text: status };
    return <Tag color={cfg.color} style={{ fontWeight: 600 }}>{cfg.text}</Tag>;
  };

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      {loading && (
        <div className="full-page-loader">
          <Spin size="large" />
        </div>
      )}

      <div className="tabled">
        <Row gutter={[24, 0]}>
          <Col xs={24} xl={24}>
            <Card
              bordered={false}
              className="criclebox tablespace mb-24"
              title={
                <span style={{ color: '#1e293b', fontWeight: 700, fontSize: 15 }}>
                  <DeleteOutlined style={{ marginRight: 8 }} />
                  Trash Payment List
                </span>
              }
              headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}
              extra={
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  {hasPermission('/payments', 'downloadPermission') && (
                    <Button
                      icon={<FileExcelOutlined />}
                      onClick={exportToExcel}
                      size="small"
                      style={{ background: 'rgba(255,255,255,0.15)', borderColor: 'rgba(255,255,255,0.4)', color: '#fff', fontWeight: 600 }}
                    >
                      Excel Download
                    </Button>
                  )}
                  <Link to="/payments">
                    <Button
                      icon={<ArrowLeftOutlined />}
                      size="small"
                      style={{ background: 'rgba(255,255,255,0.15)', borderColor: 'rgba(255,255,255,0.4)', color: '#fff', fontWeight: 600 }}
                    >
                      Back to Payments
                    </Button>
                  </Link>
                </div>
              }
            >
              <div className="table-responsive payment-table">
                <Table
                  columns={columns}
                  rowClassName={rowClassName}
                  scroll={{ x: 'max-content' }}
                  bordered
                  title={() => (
                    <div>
                      {/* ── Filter Section ──────────────────────────────────── */}
                      <div style={{
                        background: '#fafafa',
                        padding: '8px 12px',
                        borderRadius: 4,
                        border: '1px solid #e8e8e8',
                        marginBottom: 8,
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <FilterOutlined style={{ color: '#1890ff' }} />
                            <Checkbox
                              checked={showPaymentFilter}
                              onChange={e => setShowPaymentFilter(e.target.checked)}
                            >
                              <span style={{ fontWeight: 600, fontSize: 13 }}>Payment Filters</span>
                            </Checkbox>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 12, color: '#666' }}>Per page:</span>
                            <Select value={limit} onChange={handleLimitChange} size="small" style={{ width: 70 }}>
                              {[20, 40, 60, 80, 100, 150, 200].map(v => (
                                <Option key={v} value={v}>{v}</Option>
                              ))}
                            </Select>
                          </div>
                        </div>

                        {showPaymentFilter && (
                          <Row gutter={[10, 10]} style={{ marginTop: 12 }}>
                            <Col xs={24} sm={12} md={6} lg={4}>
                              <Input
                                placeholder="Phone"
                                size="small"
                                value={filters.phone}
                                onChange={e => handleFilterChange('phone', e.target.value)}
                              />
                            </Col>
                            <Col xs={24} sm={12} md={6} lg={4}>
                              <Input
                                placeholder="Name"
                                size="small"
                                value={filters.name}
                                onChange={e => handleFilterChange('name', e.target.value)}
                              />
                            </Col>
                            <Col xs={24} sm={12} md={6} lg={3}>
                              <Input
                                placeholder="Receipt No."
                                size="small"
                                value={filters.receipt_no}
                                onChange={e => handleFilterChange('receipt_no', e.target.value)}
                              />
                            </Col>
                            <Col xs={24} sm={12} md={6} lg={3}>
                              <Input
                                placeholder="Amount"
                                size="small"
                                value={filters.amount}
                                onChange={e => handleFilterChange('amount', e.target.value)}
                              />
                            </Col>
                            <Col xs={24} sm={12} md={6} lg={4}>
                              <Item className="custom-form-item" style={{ margin: 0 }}>
                                <AutoComplete
                                  size="small"
                                  value={filters.utr_number}
                                  defaultValue={filters.utr_number}
                                  placeholder="UTR / Txn ID"
                                  options={optionSuggestion}
                                  onChange={value => {
                                    const v = value.replace(/\s+/g, '');
                                    handleFilterChange('utr_number', v);
                                  }}
                                  onSearch={text => getPanelValue(text.replace(/\s+/g, ''), 'utr_number', 'sn_payments')}
                                />
                              </Item>
                            </Col>
                            <Col xs={24} sm={12} md={6} lg={4}>
                              <Select
                                size="small"
                                style={{ width: '100%' }}
                                placeholder="Account"
                                value={filters.bank_account || undefined}
                                onChange={v => handleFilterChange('bank_account', v)}
                                allowClear
                              >
                                {bankAccounts.map(acc => (
                                  <Option key={acc.id} value={String(acc.id)}>{acc.account_name}</Option>
                                ))}
                              </Select>
                            </Col>
                            <Col xs={24} sm={12} md={6} lg={4}>
                              <Select
                                size="small"
                                style={{ width: '100%' }}
                                placeholder="Mode of Payment"
                                value={filters.pyament_mode_type || undefined}
                                onChange={v => handleFilterChange('pyament_mode_type', v)}
                                allowClear
                              >
                                <Option value="UPI">UPI</Option>
                                <Option value="Cash">Cash</Option>
                                <Option value="Cheque">Cheque</Option>
                                <Option value="RTGS-NEFT">RTGS/NEFT</Option>
                                <Option value="Website-Razorpay">Website-Razorpay</Option>
                                <Option value="Application-Razorpay">Application-Razorpay</Option>
                              </Select>
                            </Col>
                            <Col xs={24} sm={12} md={6} lg={4}>
                              <Select
                                size="small"
                                style={{ width: '100%' }}
                                placeholder="Payment Status"
                                value={filters.payment_virify || undefined}
                                onChange={v => handleFilterChange('payment_virify', v)}
                                allowClear
                              >
                                <Option value="pending">Pending</Option>
                                <Option value="verified">Verified</Option>
                                <Option value="failed">Failed</Option>
                              </Select>
                            </Col>
                            <Col xs={24} sm={12} md={6} lg={4}>
                              <DatePicker
                                size="small"
                                style={{ width: '100%' }}
                                className="filter_dob"
                                format="DD-MM-YYYY"
                                value={filters.transaction_date ? moment(filters.transaction_date, 'DD-MM-YYYY') : null}
                                onChange={(_, ds) => handleFilterChange('transaction_date', ds)}
                                placeholder="Txn Date"
                              />
                            </Col>
                            <Col xs={24} sm={12} md={6} lg={4}>
                              <DatePicker
                                size="small"
                                style={{ width: '100%' }}
                                className="filter_dob"
                                format="DD-MM-YYYY"
                                value={filters.payment_entry_date ? moment(filters.payment_entry_date, 'DD-MM-YYYY') : null}
                                onChange={(_, ds) => handleFilterChange('payment_entry_date', ds)}
                                placeholder="Pay Entry Date"
                              />
                            </Col>
                            <Col xs={24} sm={12} md={6} lg={4}>
                              <Input
                                size="small"
                                placeholder="C/R-Receipt"
                                value={filters.e_reciept_no}
                                onChange={e => handleFilterChange('e_reciept_no', e.target.value)}
                              />
                            </Col>
                            <Col xs={24} sm={12} md={6} lg={4}>
                              <DatePicker
                                size="small"
                                style={{ width: '100%' }}
                                className="filter_dob"
                                format="DD-MM-YYYY"
                                value={filters.e_reciept_date ? moment(filters.e_reciept_date, 'DD-MM-YYYY') : null}
                                onChange={(_, ds) => handleFilterChange('e_reciept_date', ds)}
                                placeholder="C/R-Date"
                              />
                            </Col>
                            <Col xs={24} style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                              <Button
                                type="primary"
                                size="small"
                                icon={<SearchOutlined />}
                                onClick={handleSearchReport}
                                style={{ backgroundColor: '#7a0000', borderColor: '#7a0000' }}
                              >
                                Search
                              </Button>
                              <Button
                                size="small"
                                icon={<ReloadOutlined />}
                                onClick={handleResetFilters}
                              >
                                Reset
                              </Button>
                            </Col>
                          </Row>
                        )}
                      </div>
                    </div>
                  )}

                  dataSource={paymentList.map((payment, index) => ({
                    key: (limit * (currentPage - 1)) + index + 1,
                    payment_id: payment.payment_id,
                    is_payment_check: payment.is_payment_check,

                    name: (
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>
                          <Link to={`/users/view/${payment.user_id}`}>
                            {payment.name}
                          </Link>
                        </div>
                        <div style={{ color: '#888', fontSize: 12 }}>{payment.phone_no}</div>
                      </div>
                    ),

                    receipt_no: (
                      <Text strong>{payment.receipt_no || <span style={{ color: '#bbb' }}>—</span>}</Text>
                    ),

                    amount: (
                      <Text strong style={{ color: '#7a0000' }}>₹{payment.amount}</Text>
                    ),

                    utr_number: (
                      <Text style={{ fontSize: 12 }}>{payment.utr_number || <span style={{ color: '#bbb' }}>—</span>}</Text>
                    ),

                    transaction_date: (
                      <Text style={{ fontSize: 12 }}>
                        {payment.transaction_date ? moment(payment.transaction_date).format('DD MMM YYYY') : <span style={{ color: '#bbb' }}>—</span>}
                      </Text>
                    ),

                    duration_type: (
                      <Tag style={{ fontSize: 11 }}>{payment.duration_type}</Tag>
                    ),

                    announced_amount: (
                      <Text style={{ fontSize: 12 }}>₹{payment.announced_amount}</Text>
                    ),

                    bank_account: (
                      <Text style={{ fontSize: 11 }}>{payment.bank_account}</Text>
                    ),

                    payment_entry_date: (
                      <Text style={{ fontSize: 12 }}>
                        {payment.payment_entry_date ? moment(payment.payment_entry_date).format('DD MMM YYYY') : <span style={{ color: '#bbb' }}>—</span>}
                      </Text>
                    ),

                    firstName: (
                      <Text style={{ fontSize: 12 }}>{payment.firstName} {payment.lastName}</Text>
                    ),

                    payment_virify: statusTag(payment.payment_virify),

                    action: (
                      <Space size={4}>
                        {hasPermission('/trash-payments', 'deletePermission') && (
                          <Button
                            size="small"
                            icon={<UndoOutlined />}
                            onClick={() => showRestoreConfirm(payment.payment_id, payment.name)}
                            style={{
                              backgroundColor: '#52c41a',
                              borderColor: '#52c41a',
                              color: '#fff',
                              fontWeight: 600,
                              fontSize: 12,
                            }}
                          >
                            Restore
                          </Button>
                        )}
                      </Space>
                    ),
                  }))}

                  pagination={{
                    ...pagination,
                    showTotal: total => `Total ${total} records`,
                    style: { marginRight: 20 },
                  }}
                  onChange={handleTableChange}
                  className="ant-border-space"
                />
              </div>
            </Card>
          </Col>
        </Row>

        {sendPaynmentNotice && (
          <Invoice paymentId={sendPaynmentNotice} btnShow={['whatsappSend']} resetFxn={handleResendBtn} />
        )}
      </div>
    </>
  );
}

export default TrashPaymentList;
